import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import LifeCheckRecordList from '../../components/Records/LifeCheck/LifeCheckRecordList';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';

interface RouteParams {
  institutionId: string;
  userId: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  lifecheck: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const LifeCheckRecordListContainer: React.FC<Props> = ({
  institution,
  userListData,
  lifecheck,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId } = useParams<RouteParams>();
  const initializedRef = useRef(false);
  const userFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && !initializedRef.current) {
      initializedRef.current = true;
      recordActions.fetchLifeCheckRecords(uniqueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  const handleFetch = useCallback(
    (id: string) => {
      recordActions.fetchLifeCheckRecords(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDelete = useCallback(
    (id: string, sequence: number) => {
      recordActions.deleteLifeCheckRecord(id, sequence);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetLifeCheckFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <LifeCheckRecordList
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      records={lifecheck.list}
      executing={lifecheck.executing}
      loaded={lifecheck.loaded}
      deleted={lifecheck.deleted}
      onFetch={handleFetch}
      onDelete={handleDelete}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    lifecheck: state.records.lifecheck,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LifeCheckRecordListContainer);
